import {html} from "lit";
import {Interval} from "luxon";
import {container, inject, singleton} from "tsyringe";
import {ProxyService} from "../../advanced-form/proxy.service";
import {NOTIFICATION_DURATION} from "../../constants";
import {Issue} from "../../domain/entities/issue.entity";
import {UiHelper} from "../../helpers/ui.helper";
import {IntervalLoadingGuard} from "../../services/interval-loading.guard";
import {AbstractBaseListener} from "../abstract-base.listener";
import {BaseMessage} from "../base-message.interface";
import {OnMessage} from "../on-message.decorator";
import {EntityDeletedPayload} from "../payloads/entity-deleted.payload";
import {IssueRepository} from "../../domain/repositories/issue.repository";
import {EntityManager} from "../../persistence/entity.manager";
import {isMilestoneDeletion, projectsToRefresh} from "./is-milestone-deletion";

const filterOnIssueEntityFn = (message: BaseMessage<EntityDeletedPayload<Issue>>): boolean => {
    // noinspection SuspiciousTypeOfGuard
    return message.payload.entity instanceof Issue;
}

@singleton()
export class EntityDeletedListener extends AbstractBaseListener {

    constructor(
        @inject(IntervalLoadingGuard) private readonly _intervalLoadingGuard: IntervalLoadingGuard,
        @inject(ProxyService) private readonly _proxyService: ProxyService,
        @inject(EntityManager) private readonly _entityManager: EntityManager
    ) {
        super();
    }

    @OnMessage(`entity.deleted`, filterOnIssueEntityFn, 10)
    public async onIssueDeleted(messages: Array<BaseMessage<EntityDeletedPayload<Issue>>>): Promise<void> {
        const intervals = Interval.merge(messages.map(m => {
            const issue = m.payload.entity;
            if (this._proxyService.has(issue.uid)) {
                this._proxyService.delete(issue.uid);
            }
            issue.commit(); // to set last tick of issue
            return m.payload.entity.timeRange
        }));

        for (const interval of intervals) {
            this._intervalLoadingGuard.removeRange(interval);
        }

        for (const message of messages) {
            const entity = message.payload.entity;
            // Remove deleted entity from EntityManager cache to prevent it from showing up in UI
            this._entityManager.unloadEntity(entity);
            // Notification now handled by GlobalNotificationService
        }
    }

    @OnMessage(`entity.deleted`, isMilestoneDeletion)
    public async onMilestoneDeleted(messages: Array<BaseMessage<EntityDeletedPayload<Issue>>>): Promise<void> {
        const issues = messages.map(m => m.payload.entity);
        const issueRepository = container.resolve(IssueRepository);
        for (const project of projectsToRefresh(issues, uid => this._entityManager.isLoaded(uid))) {
            project.milestones = issueRepository.findAllMilestonesByProject(project.uid);
        }

        for (const interval of Interval.merge(issues.map(i => i.timeRange))) {
            this._intervalLoadingGuard.removeRange(interval);
        }
    }
}